import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import "./Style.css";
import Logo from "../images/svc-central-logo.jpg";
import envVariable from "../EnvironmentVariables.json";
import { useNotificationListener, disconnectNotifications } from "../utils/ws/useNotificationListener";

const isWsDebugEnabled = () => {
  try {
    return localStorage.getItem("SCO_WS_DEBUG") === "1";
  } catch (_) {
    return false;
  }
};

const MenuBar = () => {
  const location = useLocation();
  // Initialize from sessionStorage for instant display across navigation
  const [notificationCount, setNotificationCount] = useState(() => {
    const cached = sessionStorage.getItem('sco_notification_count');
    return cached ? parseInt(cached, 10) : 0;
  });
  const [disputeCount, setDisputeCount] = useState(0); // NEW state for disputes

  // Use WebSocket for real-time notification updates
  useNotificationListener({
    onNotification: () => {
      // Immediately refresh the notification count
      fetchNotificationCount();
    },
    autoConnect: true,
  });

  // Fetch notifications count
  const fetchNotificationCount = async () => {
    try {
      // Read userId fresh each time to handle login/logout correctly
      const userId = localStorage.getItem("User_Id");
      if (!userId) {
        if (isWsDebugEnabled()) {
          console.warn('[MenuBar] No User_Id in localStorage, skipping notification count fetch');
        }
        return;
      }

      const response = await fetch(`${envVariable.serverId}/SCO/getNotificationCount.php?user_id=${userId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch notification count: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.status === "Success") {
        const count = data.count;
        setNotificationCount(count);
        // Cache in sessionStorage for persistence across page navigation
        sessionStorage.setItem('sco_notification_count', count.toString());
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error("Error fetching notification count:", error);
    }
  };

  // NEW: fetch the number of unread disputes
  const fetchDisputeCount = async () => {
    // Read scId fresh each time
    const scId = localStorage.getItem("SC_Id");
    if (!scId) return;

    try {
      const res = await fetch(`${envVariable.serverId}/SCO/getDisputeCount.php?sc_id=${scId}`);
      const data = await res.json();
      if (data.status === "Success") setDisputeCount(data.count);
    } catch (e) {
      console.error("dispute count error", e);
    }
  };

  useEffect(() => {
    fetchNotificationCount();
    fetchDisputeCount(); // also fetch disputes

    // Expose global refresh function so Notifications page can trigger immediate update
    window.refreshSCONotificationBadge = () => {
      fetchNotificationCount();
    };

    return () => {
      delete window.refreshSCONotificationBadge;
    };
  }, []);

  // Utility to highlight active tab
  const getNavLinkClass = (path) => {
    return location.pathname.startsWith(path) ? "menubar-item active" : "menubar-item";
  };

  // On logout
  const handleLogout = () => {
    localStorage.removeItem("sc_name");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("User_Id");
    localStorage.removeItem("SC_Id");
    localStorage.removeItem("@api_key");
    localStorage.removeItem("api_key");
    try { disconnectNotifications(); }
    catch (e) {
      if (isWsDebugEnabled()) {
        console.debug('disconnectNotifications failed', e);
      }
    }
    // Clear cached notification count on logout
    sessionStorage.removeItem('sco_notification_count');
  };

  return (
    <div className="menubar-container">
      <div className="menubar-logo">
        <img src={Logo} className="logo" alt="Logo" />
        <div className="mainbar">
          <div className="menubar">
            <NavLink
              to="/account"
              className={({ isActive }) => (isActive ? "menubar-item active" : "menubar-item")}
            >
              Profile
            </NavLink>

            <NavLink
              to="/employees"
              className={({ isActive }) => (isActive ? "menubar-item active" : "menubar-item")}
            >
              Employees
            </NavLink>

            <NavLink to="/tasks" className={() => getNavLinkClass("/tasks")}>
              Tasks
            </NavLink>

            <NavLink
              to="/calendar"
              className={({ isActive }) => (isActive ? "menubar-item active" : "menubar-item")}
            >
              Calendar
            </NavLink>

            <NavLink
              to="/request-vacation-sick"
              className={({ isActive }) => (isActive ? "menubar-item active" : "menubar-item")}
            >
              Leave Requests
            </NavLink>

            {/* NEW: Disputes link with a red badge if disputeCount > 0 */}
            <NavLink
              to="/disputes"
              className={({ isActive }) => (isActive ? "menubar-item badge-wrapper active" : "menubar-item badge-wrapper")}
            >
              Disputes
              {disputeCount > 0 && <span className="badge-circle">{disputeCount}</span>}
            </NavLink>
          </div>

          <div className="menubar-logout">
            {/* Notification link */}
            <NavLink
              to="/notification"
              className={({ isActive }) => (isActive ? "menubar-item-icon active" : "menubar-item-icon")}
              aria-label="Notifications"
            >
              <div className="notification-icon">
                <NotificationsNoneRoundedIcon fontSize="medium" />
                {notificationCount > 0 && (
                  <span className="notification-count">{notificationCount}</span>
                )}
              </div>
            </NavLink>

            {/* Logout link */}
            <NavLink
              to="/"
              className={({ isActive }) => (isActive ? "menubar-item logout-link active" : "menubar-item logout-link")}
              onClick={handleLogout}
            >
              <LogoutRoundedIcon fontSize="small" />
              Logout
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuBar;
